import { useState } from "react";
import { env } from "@/lib/env";

/**
 * Locked-window "Export my data" affordance (PDR 011 §6, #4206 export-on-lock).
 *
 * Downloads the portable takeout ZIP (objects as JSON/CSV + one mbox per
 * connected mail account) from `GET /takeout`. That endpoint is intentionally
 * NoPaywall, so it is reachable for a locked/expired trial — letting the user
 * take their data before the irreversible day-21 hard delete.
 *
 * Binary blob download (not a typed api client) — same pattern as
 * SelectiveExportImportPanel's zip export.
 */
export function ExportMyDataButton({ authToken }: { authToken: string | null }) {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    if (!authToken) {
      setError("Your session expired—please log in again to export.");
      return;
    }
    setIsExporting(true);
    setError(null);
    try {
      const res = await fetch(`${env.apiBaseUrl}/takeout`, {
        method: "GET",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        throw new Error(`Export failed (${res.status})`);
      }

      const disposition = res.headers.get("Content-Disposition");
      let filename = "xavior-takeout.zip";
      const match = disposition && /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="text-center">
      <button
        type="button"
        onClick={handleExport}
        disabled={isExporting}
        className="text-sm font-medium text-gray-600 underline underline-offset-2 transition-colors hover:text-gray-900 disabled:opacity-50"
      >
        {isExporting ? "Preparing your export…" : "Export my data"}
      </button>
      <p className="mt-1 text-xs text-gray-400">
        Download your objects and mail before your trial data is removed.
      </p>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}
